/* eslint-disable @typescript-eslint/no-explicit-any */
import {CommonModule} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  Optional,
  Self,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NgControl,
} from '@angular/forms';
import {TuiIcon, TuiTextfield} from '@taiga-ui/core';
import {
  TuiChevron,
  TuiDataListWrapper,
  TuiSelect,
  TuiTooltip,
} from '@taiga-ui/kit';
import {ErrorMessageComponent} from '../../v1/error-component/error-message.component';
import {GateweeSkeletonComponent} from '../../v1/gatewee-skeleton/gatewee-skeleton.component';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'gatewee-select-v3',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    TuiChevron,
    TuiDataListWrapper,
    TuiIcon,
    TuiSelect,
    TuiTextfield,
    TuiTooltip,
    ErrorMessageComponent,
    GateweeSkeletonComponent,
    TranslateModule,
  ],
  templateUrl: './gatewee-select-v3.component.html',
  styleUrls: ['./gatewee-select-v3.component.css'],
})
export class GateweeSelectV3Component
  implements ControlValueAccessor, OnChanges
{
  @Input() label = '';
  /** CSS classes for the label (when `label` is set). Defaults match legacy `gw-form-label` look. */
  @Input() labelClass = 'gw-form-label gw-h-[16px]';
  /** When set, used as native input `id` / `name` (and label `for`). Overrides `NgControl.name`-based id. */
  @Input() inputId = '';
  @Input() placeholder = 'Placeholder...';
  @Input() iconStart = '';
  @Input() toolTip = '';
  @Input() required = false;
  @Input() height = '72px';
  @Input() loading = false;

  @Input() items: any[] = [];
  @Input() customMessages: Record<string, string> = {};
  @Input() class = '';

  value: any = null;
  disabled = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  readonly stringify = (item: any): string => {
    const label = item?.label ?? '';
    if (!label) return '';
    return this.translate?.instant
      ? this.translate.instant(label)
      : String(label);
  };

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private cd: ChangeDetectorRef,
    private readonly translate: TranslateService,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get control(): FormControl | null {
    return this.ngControl?.control as FormControl;
  }

  get effectiveInputId(): string {
    const explicit = this.inputId?.trim();
    if (explicit) {
      return explicit;
    }
    const n = this.ngControl?.name;
    if (n !== null && n !== undefined && String(n).trim() !== '') {
      return `gatewee-select-${String(n)}`;
    }
    return 'gatewee-select';
  }

  ngOnChanges(): void {
    this.cd.markForCheck();
  }

  writeValue(value: any): void {
    if (value && this.items?.length) {
      this.value = this.items.find(i => i.value === value) ?? value;
    } else {
      this.value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleChange(item: any): void {
    const pureValue = item?.value ?? item;

    this.value = item;

    this.onChange(pureValue);

    this.markTouched();
  }

  clearValue(): void {
    this.value = null;
    this.onChange(null);

    this.markTouched();
  }

  markTouched(): void {
    this.onTouched();
    this.control?.markAsTouched();
  }
}
